import { Component, OnInit, Input } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { RiderVehicleTypeEnum, ServiceTypeEnum } from 'app/models';
import { ConfigService, DashboardService, AppDataService } from 'app/services';
import moment from 'moment';
import { Utils } from 'app/utils';

@Component({
    selector: 'accounting-dashboard',
    templateUrl: './accounting-dashboard.component.html',
    styleUrls: ['./accounting-dashboard.component.scss'],
    providers: [MessageService]
})
export class AccountingDashboardComponent implements OnInit {
    @Input()
    public menuPermissionActive!: string;

    public accountingDashboard: any = {};
    public selectedDateRange: Date[] = [];

    public totalRevenueChartData: any;
    public totalRevenueChartOptions: any;

    public totalExpensesChartData: any;
    public totalNegativeExpensesChartData: any;
    public totalExpensesChartOptions: any;

    public totalProfitChartData: any;
    public totalProfitChartOptions: any;

    public totalCashflowChartData: any;
    public totalCashflowChartOptions: any;

    public totalTransactionVolumeChartData: any;
    public totalTransactionVolumeChartOptions: any;

    public totalPaymentMethodChartData: any;
    public totalPaymentMethodChartOptions: any;

    public totalOrderDistributionChartData: any;
    public totalOrderDistributionChartOptions: any;
    public breadcrumbs: MenuItem[] = [{ icon: 'icon_finance' }, { label: 'Finances' }, { label: 'Accounting' }, { label: 'Dashboard' }];

    constructor(public configService: ConfigService,
        public dashboardService: DashboardService,
        public messageService: MessageService,
        public appDataService: AppDataService) { }

    ngOnInit(): void {
        this.selectedDateRange[0] = new Date();
        this.selectedDateRange[0].setDate(new Date().getDate() - 7);
        this.selectedDateRange[1] = new Date();
        this.getTransactionAccountingDashboard();
    }

    public changeDateRange() {
        if (!this.selectedDateRange[1]) {
            return;
        }
        this.getTransactionAccountingDashboard();
    }

    public getTransactionAccountingDashboard() {
        const dto = {
            StartDate: moment(this.selectedDateRange[0]).format('YYYY-MM-DD'),
            EndDate: moment(this.selectedDateRange[1]).format('YYYY-MM-DD'),
            ClientLocationType: this.appDataService.getClientLocationType(),
            TimeZoneOffset: Utils.getTimezoneOffset()
        };

        this.dashboardService.getTransactionAccountingDashboard(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.accountingDashboard = data;
                this.accountingDashboard.TotalRevenue = `₱ ${this.accountingDashboard.TotalRevenue.toFixed(2)}`;
                this.accountingDashboard.TotalExpenses = `₱ ${this.accountingDashboard.TotalExpenses.toFixed(2)}`;
                this.accountingDashboard.TotalProfit = `₱ ${this.accountingDashboard.TotalProfit.toFixed(2)}`;
                this.accountingDashboard.TotalTransactionVolume = `₱ ${this.accountingDashboard.TotalTransactionVolume.toFixed(2)}`;

                this.totalRevenueChartInit();
                this.totalExpensesChartInit();
                this.totalProfitChartInit();
                this.cashflowChartInit();
                this.transactionVolumeChartInit();
                this.paymentMethodChartInit();
                this.orderDistributionChartInit();
            });
    }

    public totalRevenueChartInit(): void {
        this.totalRevenueChartData = {
            labels: this.accountingDashboard.DateLabels,
            datasets: [
                {
                    data: this.accountingDashboard.TotalRevenueData,
                    fill: true,
                    borderColor: '#FFB100',
                    tension: .4,
                    backgroundColor: 'rgba(255,177,0,0.2)'
                }
            ]
        };

        this.totalRevenueChartOptions = {
            plugins: {
                legend: {
                    display: false
                }
            }
        };
    }

    public totalExpensesChartInit(): void {
        this.totalExpensesChartData = {
            labels: this.accountingDashboard.DateLabels,
            datasets: [
                {
                    data: this.accountingDashboard.TotalExpensesData,
                    fill: true,
                    borderColor: '#044362',
                    tension: .4,
                    backgroundColor: 'rgba(4, 67, 98, 0.2)'
                }
            ]
        };

        this.totalExpensesChartOptions = {
            plugins: {
                legend: {
                    display: false
                }
            }
        };
    }

    public totalProfitChartInit(): void {
        this.totalProfitChartData = {
            labels: this.accountingDashboard.DateLabels,
            datasets: [
                {
                    data: this.accountingDashboard.TotalProfitData,
                    fill: true,
                    borderColor: '#26bd76',
                    tension: .4,
                    backgroundColor: 'rgba(227, 246, 237, 0.7)'
                }
            ]
        };

        this.totalProfitChartOptions = {
            plugins: {
                legend: {
                    display: false
                }
            }
        };
    }

    public cashflowChartInit(): void {
        this.totalCashflowChartData = {
            labels: this.accountingDashboard.DateLabels,
            datasets: [
                {
                    type: 'line',
                    data: this.accountingDashboard.TotalProfitData,
                    label: 'Profit',
                    fill: true,
                    borderColor: '#26bd76',
                    tension: .4,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                {
                    type: 'bar',
                    data: this.accountingDashboard.TotalRevenueData,
                    label: 'Revenue',
                    fill: true,
                    borderColor: 'white',
                    borderWidth: 2,
                    backgroundColor: 'rgba(253, 215, 106, 1)'
                },
                {
                    type: 'bar',
                    data: this.accountingDashboard.TotalNegativeExpensesData,
                    label: 'Expenses',
                    fill: true,
                    borderColor: 'white',
                    borderWidth: 2,
                    backgroundColor: '#044362'
                }
            ]
        };

        this.totalCashflowChartOptions = {
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: '#333333',
                        usePointStyle: true
                    }
                },
            },
            scales: {
                x: {
                    stacked: true
                },
                y: {
                    stacked: true
                }
            }
        };
    }

    public transactionVolumeChartInit(): void {
        this.totalTransactionVolumeChartData = {
            labels: this.accountingDashboard.DateLabels,
            datasets: [
                {
                    type: 'line',
                    data: this.accountingDashboard.TotalRevenueData,
                    label: 'Revenue',
                    fill: true,
                    borderColor: '#044362',
                    borderWidth: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                {
                    type: 'line',
                    data: this.accountingDashboard.TotalProfitData,
                    label: 'Profit',
                    fill: true,
                    borderDash: [5, 5],
                    borderColor: '#1cba70',
                    tension: .4,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
            ]
        };

        this.totalTransactionVolumeChartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 1,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: '#333333',
                        usePointStyle: false
                    }
                },
            },
            scales: {
                x: {
                    ticks: {
                        color: '#333333'
                    },
                    grid: {
                        color: '#dfe7ef',
                        drawBorder: false
                    }
                },
                y: {
                    ticks: {
                        color: '#333333'
                    },
                    grid: {
                        color: '#dfe7ef',
                        drawBorder: false
                    }
                }
            }
        };
    }

    public paymentMethodChartInit(): void {
        this.totalPaymentMethodChartData = {
            labels: ['Gcash', 'Xpay', 'Credit', 'Maya', 'Cash'],
            datasets: [
                {
                    data: [this.accountingDashboard.GcashPercent, this.accountingDashboard.XpayPercent, this.accountingDashboard.CreditPercent,
                        this.accountingDashboard.MayaPercent, this.accountingDashboard.CashPercent],
                    backgroundColor: ['#3396fc', '#eb4236', '#00c0f0', '#323232', '#ffb100'],
                    hoverBackgroundColor: ['#3396fc', '#eb4236', '#00c0f0', '#323232', '#ffb100']
                }
            ]
        };

        this.totalPaymentMethodChartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    position: 'left',
                    labels: {
                        usePointStyle: true,
                        color: '#333'
                    }
                }
            }
        };
    }

    public orderDistributionChartInit(): void {
        this.totalOrderDistributionChartData = {
            labels: this.accountingDashboard.DateLabels,
            datasets: [
                {
                    type: 'line',
                    data: this.accountingDashboard.TotalOrderDistributionWeekendsData,
                    label: 'Weekends',
                    fill: true,
                    borderColor: '#3396fc',
                    borderWidth: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                {
                    type: 'line',
                    data: this.accountingDashboard.TotalOrderDistributionWeekdaysData,
                    label: 'Weekdays',
                    fill: true,
                    borderColor: '#1cba70',
                    borderWidth: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                },
                {
                    type: 'line',
                    data: this.accountingDashboard.TotalOrderDistributionHolidayData,
                    label: 'Holiday',
                    fill: true,
                    borderColor: '#eb4236',
                    borderWidth: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
            ]
        };

        this.totalOrderDistributionChartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 1,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: '#333333',
                        usePointStyle: true
                    }
                },
            },
            scales: {
                x: {
                    ticks: {
                        color: '#333333'
                    },
                    grid: {
                        color: '#dfe7ef',
                        drawBorder: false
                    }
                },
                y: {
                    ticks: {
                        color: '#333333'
                    },
                    grid: {
                        color: '#dfe7ef',
                        drawBorder: false
                    }
                }
            }
        };
    }
}
